import React, { FunctionComponent, CSSProperties, useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Media, RootStore } from '../app/types';
import { updateMedia } from '../app/actions';
import axios from 'axios';

import { MediaThumb } from './MediaThumb';
import upArrow from '../images/up_open.svg';
import Modal from 'react-modal';
Modal.setAppElement('#root');

const modalStyle = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: 12,
        padding: 0,
        border: 'none',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    }
};

const hScrollList: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    overflowX: 'auto',
    overflowY: 'hidden',
}

const hScrollItem: CSSProperties = {
    flexShrink: 0,
}

const hScrollIcon: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 100,
    height: 100,
    border: '1px dashed #ccc',
    borderRadius: 8,
    cursor: 'pointer',
    flexShrink: 0,
    backgroundColor: '#fafafa',
}

interface MediaGalleryProps {
    collectionID: number
    articleID: number
    onInjectHeader: any
}

export const MediaGallery: FunctionComponent<MediaGalleryProps> = (props) => {
    const dispatch = useDispatch();
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const allMedia = useSelector((state: RootStore) => state.media);

    const mediaList = useMemo(() =>
        allMedia.filter((m: Media) => m.collectionID === props.collectionID),
        [allMedia, props.collectionID]
    );

    useEffect(() => {
        axios.get('/api/v1/collection/'+props.collectionID+'/media').then(result=>{
            dispatch(updateMedia(result.data.media));
        })
    }, [dispatch, props.collectionID]);


    const onClickUploadFile = () => {
        if (!uploadFile) return;
        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        const config = {
            headers: {
                'content-type': 'multipart/form-data'
            }
        };
        const url = '/api/v1/media/file?collectionID=' + props.collectionID + '&articleID=' + props.articleID;
        return axios.post(url, formData, config).then(response => {
            setIsUploadModalOpen(false);
            setUploadFile(null);
            setIsUploading(false);
            let media: Media[] = [response.data.media];
            dispatch(updateMedia(media as []));
        }).catch(() => {
            setIsUploading(false);
        });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) setUploadFile(file);
    };

    const closeUploadModal = () => {
        setIsUploadModalOpen(false);
        setUploadFile(null);
        setIsDragging(false);
    };

    let onMediaItemclick = (media: Media) => {
        setSelectedMedia(media);
        setIsMediaModalOpen(true);
    }

    let onClickInject = () => {
        if (props.onInjectHeader) {
            props.onInjectHeader(selectedMedia);
        }
    };

    return <>
        <Modal
            isOpen={isUploadModalOpen}
            onRequestClose={closeUploadModal}
            style={modalStyle}
            contentLabel="Upload Modal"
        >
            <div style={{ width: 400, maxWidth: '100%' }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    borderBottom: '1px solid #eee',
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Upload Media</h3>
                    <button
                        onClick={closeUploadModal}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#999',
                            lineHeight: 1,
                        }}
                    >
                        &times;
                    </button>
                </div>

                {/* Drop Zone */}
                <div style={{ padding: 20 }}>
                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            border: `2px dashed ${isDragging ? '#0d6efd' : '#ddd'}`,
                            borderRadius: 8,
                            padding: 32,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={e => setUploadFile(e.target.files?.[0] || null)}
                            style={{ display: 'none' }}
                        />
                        <img
                            src={upArrow}
                            alt="upload"
                            style={{ width: 48, height: 48, opacity: 0.4, marginBottom: 12 }}
                        />
                        <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>
                            {uploadFile ? uploadFile.name : 'Drop an image here or click to browse'}
                        </p>
                        {uploadFile && (
                            <p style={{ margin: '8px 0 0', color: '#999', fontSize: '0.85rem' }}>
                                {(uploadFile.size / 1024).toFixed(1)} KB
                            </p>
                        )}
                    </div>

                    {/* Preview */}
                    {uploadFile && (
                        <div style={{ marginTop: 16, textAlign: 'center' }}>
                            <img
                                src={URL.createObjectURL(uploadFile)}
                                alt="preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 150,
                                    borderRadius: 8,
                                    border: '1px solid #eee',
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    padding: '16px 20px',
                    borderTop: '1px solid #eee',
                    backgroundColor: '#fafafa',
                }}>
                    <button
                        onClick={closeUploadModal}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onClickUploadFile}
                        disabled={!uploadFile || isUploading}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            borderRadius: 6,
                            backgroundColor: uploadFile ? '#0d6efd' : '#ccc',
                            color: '#fff',
                            cursor: uploadFile ? 'pointer' : 'not-allowed',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                        }}
                    >
                        {isUploading ? 'Uploading...' : 'Upload'}
                    </button>
                </div>
            </div>
        </Modal>

        <Modal
            isOpen={isMediaModalOpen}
            onRequestClose={() => setIsMediaModalOpen(false)}
            style={modalStyle}
            contentLabel="Media Details"
        >
            <div style={{ maxWidth: 700 }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    borderBottom: '1px solid #eee',
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                        {selectedMedia?.shortKey || 'Media'}
                    </h3>
                    <button
                        onClick={() => setIsMediaModalOpen(false)}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: '#999',
                            lineHeight: 1,
                        }}
                    >
                        &times;
                    </button>
                </div>

                {/* Image */}
                <div style={{ padding: 20, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                    <img
                        style={{ maxWidth: '100%', maxHeight: 450, borderRadius: 4 }}
                        src={selectedMedia?.imageURL}
                        alt={selectedMedia?.shortKey}
                    />
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 12,
                    padding: '16px 20px',
                    borderTop: '1px solid #eee',
                }}>
                    <button
                        onClick={() => setIsMediaModalOpen(false)}
                        style={{
                            padding: '8px 16px',
                            border: '1px solid #ddd',
                            borderRadius: 6,
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                        }}
                    >
                        Close
                    </button>
                    <button
                        onClick={() => { onClickInject(); setIsMediaModalOpen(false); }}
                        style={{
                            padding: '8px 20px',
                            border: 'none',
                            borderRadius: 6,
                            backgroundColor: '#0d6efd',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                        }}
                    >
                        Insert into Article
                    </button>
                </div>
            </div>
        </Modal>

        <div style={hScrollList}>
            {
                mediaList.map( (media: Media) => {
                    return <div style={hScrollItem} key={media.ID} onClick={()=> onMediaItemclick(media)}>
                        <MediaThumb mediaID={media.ID}/>
                    </div>;
                })
            }
            <div style={hScrollIcon} onClick={() => setIsUploadModalOpen(true)}>
                <img style={{ width: 32, height: 32, opacity: 0.5 }} src={upArrow} alt="upload" />
            </div>

        </div>
    </>;
}
